import React from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  LoadingButton,
  ServerPaginatedTable,
} from "../../../shared";
import InputControl from "../../../shared/components/InputControl";
import { AddCriteriaDialog, CriteriaChips } from "../components";
import { useManageSegmentation } from "../hooks";
import { Search } from "@mui/icons-material";

export const ManageSegmentation = () => {
  const {
    state,
    isEditMode,
    title,
    validator,
    breadcrumbLinks,
    onChangeField,
    toggleLogicalOperator,
    openCriteriaDialog,
    closeCriteriaDialog,
    onAddOrUpdateRule,
    onEditRule,
    onRemoveRule,
    onSearchPreview,
    onPreviewGotoPage,
    onPreviewSetPageSize,
    onSubmit,
    dropdownOptions,
    asyncDropdownOptions,
  } = useManageSegmentation();

  const previewColumns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => {
          const fullName = [row.original.first_name, row.original.last_name]
            .filter(Boolean)
            .join(" ");

          return (
            <Typography
              sx={{
                fontSize: "13px",
                color: "inherit",
                textDecoration: "underline",
                "&:hover": { color: "#0000EE", cursor: "pointer" },
              }}
              onClick={() => {
                window.open(`/customers/details/${row.original.id}`, "_blank");
              }}
            >
              {fullName || "-"}
            </Typography>
          );
        },
        id: "name",
        disableSortBy: true,
      },
      {
        Header: "Email ID",
        accessor: "email",
        disableSortBy: true,
      },
      {
        Header: "Phone Number",
        accessor: "phone",
        disableSortBy: true,
      },
    ],
    [],
  );

  if (state.bootLoading || (isEditMode && state.isDetailLoading)) {
    return <Loader />;
  }

  return (
    <ContentLayout
      title={title}
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbLinks} />}
      rightContent={
        <LoadingButton
          variant="contained"
          loading={state.isSaving}
          disabled={state.previewCustomers.length === 0}
          onClick={onSubmit}
        >
          Save
        </LoadingButton>
      }
    >
      <Stack gap={2}>
        {/* ------------------------------------------------------------------ */}
        {/* Details card                                                         */}
        {/* ------------------------------------------------------------------ */}
        <Card sx={{ p: "32px 24px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle1" fontWeight={600}>
                Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Stack gap="20px">
                <InputControl
                  name="name"
                  label="Segment Name"
                  value={state.formData.name}
                  onChange={onChangeField}
                  required
                  error={validator.current.message(
                    "Segment Name",
                    state.formData.name,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Segment Name",
                    state.formData.name,
                    "required",
                  )}
                />
                <InputControl
                  name="description"
                  label="Description"
                  value={state.formData.description}
                  onChange={onChangeField}
                  multiline
                  rows={3}
                />
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* ------------------------------------------------------------------ */}
        {/* Criteria card                                                        */}
        {/* ------------------------------------------------------------------ */}
        <Card
          sx={{
            p: "24px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack border={1} borderRadius="10px" borderColor="divider">
            {/* Criteria Type toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: 1,
                borderColor: "divider",
                height: "65px",
              }}
            >
              <Typography fontWeight={500} p="16px" fontSize="16px">
                Criteria
              </Typography>
            </Box>

            <Box
              sx={{
                p: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <ToggleButtonGroup
                value={state.formData.logical_operator}
                exclusive
                onChange={(_, val) => toggleLogicalOperator(val)}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "1px solid",
                    borderColor: "divider",
                    color: "text.primary",
                    textTransform: "none",
                    "&.Mui-selected": {
                      borderColor: "info.main",
                      backgroundColor: "info.main",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "info.dark",
                        borderColor: "info.dark",
                      },
                    },
                    height: "35px",
                  },
                }}
              >
                <ToggleButton value="AND" sx={{ px: 2 }}>
                  Match All
                </ToggleButton>
                <ToggleButton value="OR" sx={{ px: 2 }}>
                  Match Any
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Selected rules as chips */}
              {state.formData.rules.length > 0 && (
                <CriteriaChips
                  rules={state.formData.rules}
                  operators={state.bootData.operators}
                  dropdownOptions={dropdownOptions}
                  asyncDropdownOptions={asyncDropdownOptions}
                  onEditRule={onEditRule}
                  onRemoveRule={onRemoveRule}
                  allCriteria={state.bootData?.criterias}
                />
              )}

              <Button
                color="info"
                startIcon={<AddIcon />}
                onClick={openCriteriaDialog}
                disabled={state.bootLoading}
                variant="text"
                sx={{ width: "fit-content" }}
              >
                Add New Criteria
              </Button>
            </Box>
          </Stack>

          <Button
            variant="contained"
            onClick={() => onSearchPreview()}
            disabled={
              state.formData.rules.length === 0 ||
              state.previewLoading ||
              state.hasSearched
            }
            sx={{ ml: "auto" }}
            startIcon={<Search />}
          >
            Search
          </Button>

          {(state.hasSearched || state.previewLoading) && (
            <Box
              sx={{
                borderTop: "1px solid rgba(153, 153, 153, 0.12)",
                borderRadius: "8px",
              }}
            >
              <ServerPaginatedTable
                tableTitle="Customers"
                columns={previewColumns}
                data={state.previewCustomers}
                isLoading={state.previewLoading}
                pagination={state.previewPagination}
                sortByGlobal={[]}
                gotoPage={onPreviewGotoPage}
                setPageSize={onPreviewSetPageSize}
                defaultSearch={false}
                searchBar={false}
                manualSortBy={false}
                completeFeature={false}
              />
            </Box>
          )}
        </Card>
      </Stack>

      {/* ------------------------------------------------------------------ */}
      {/* Add Criteria Dialog                                                  */}
      {/* ------------------------------------------------------------------ */}
      <AddCriteriaDialog
        open={state.criteriaDialogOpen}
        onClose={closeCriteriaDialog}
        bootData={state.bootData}
        existingRules={state.formData.rules}
        editingRule={
          state.editingRuleIndex !== null
            ? state.formData.rules[state.editingRuleIndex]
            : null
        }
        onAddOrUpdateRule={onAddOrUpdateRule}
        dropdownOptions={dropdownOptions}
        asyncDropdownOptions={asyncDropdownOptions}
      />
    </ContentLayout>
  );
};
